import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Flex, Spin, ArrowLeftOutlined } from '@/shared/antd-imports';
import clientPortalAPI from '@/services/api';
import {
  IClientInput,
  IClientInputsPage,
  IInputSubmission,
  InputsChecklist,
  InputSubmitError,
} from '@/components/inputs/InputsChecklist';

interface IAxiosLikeError {
  response?: { data?: { message?: string; body?: { code?: string } } };
}

export const toSubmitError = (error: unknown, fallback: string): InputSubmitError => {
  const data = (error as IAxiosLikeError)?.response?.data;
  return new InputSubmitError(data?.message || fallback, data?.body?.code ?? null);
};

/** Portal → project → "What we need from you". Files go through the portal upload, then are attached. */
const ProjectInputsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState<IClientInputsPage | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [hasError, setError] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(false);
    try {
      const response = await clientPortalAPI.getProjectInputs<IClientInputsPage>(id);
      setPage(response.body ?? null);
      if (!response.done) setError(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (item: IClientInput, submission: IInputSubmission): Promise<IClientInput> => {
    const fallback = t('inputs.sendError', { defaultValue: 'Could not send this. Please try again.' });
    try {
      const attachmentIds: string[] = [];
      for (const file of submission.files ?? []) {
        const uploaded = await clientPortalAPI.uploadFile(file, 'document');
        if (!uploaded.done || !uploaded.body?.id) throw new InputSubmitError(t('inputs.uploadError', { defaultValue: 'A file could not be uploaded.' }), 'storage_error');
        attachmentIds.push(uploaded.body.id);
      }
      const response = await clientPortalAPI.submitProjectInput<{ item: IClientInput }>(id as string, item.id, {
        value: submission.value,
        secret: submission.secret,
        confirmed: submission.confirmed,
        attachment_ids: attachmentIds.length ? attachmentIds : undefined,
        confirm_not_secret: submission.confirm_not_secret,
      });
      if (!response.done || !response.body?.item) throw new InputSubmitError(response.message || fallback, null);
      return response.body.item;
    } catch (error) {
      if (error instanceof InputSubmitError) throw error;
      throw toSubmitError(error, fallback);
    }
  };

  return (
    <Flex vertical gap={16} style={{ maxWidth: 860 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/inputs')} style={{ alignSelf: 'flex-start' }}>
        {t('inputs.back', { defaultValue: 'All checklists' })}
      </Button>
      {isLoading && (
        <Flex justify="center" style={{ padding: 48 }}>
          <Spin size="large" />
        </Flex>
      )}
      {!isLoading && hasError && (
        <Alert
          type="error"
          showIcon
          message={t('inputs.loadError', { defaultValue: 'Could not load the checklist. Please try again.' })}
          action={<Button size="small" onClick={load}>{t('inputs.retry', { defaultValue: 'Try again' })}</Button>}
        />
      )}
      {!isLoading && !hasError && page && <InputsChecklist page={page} uploadsEnabled onSubmit={submit} />}
    </Flex>
  );
};

export default ProjectInputsPage;
